import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, X, QrCode, Printer, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Letter-size (8.5x11 @ 150dpi = 1275x1650) printable QR poster composed in <canvas>.
 * Fetches the QR PNG from /api/qr and renders it on top of branded layout.
 */
export default function QrPosterModal({ city, open, onClose, sponsorName = null, sponsorId = null, sponsorLogoUrl = null }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/hunts/${encodeURIComponent(city)}?src=poster${sponsorId ? `&sid=${encodeURIComponent(sponsorId)}` : ""}`
      : "";

  useEffect(() => {
    if (!open) return;
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, city, sponsorName, sponsorLogoUrl]);

  const loadImage = (src) =>
    new Promise((res, rej) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = src;
    });

  const draw = async () => {
    setLoading(true);
    setDataUrl(null);
    // Wait for custom fonts so poster renders with correct typography on first open
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
    } catch {
      // noop
    }
    const canvas = canvasRef.current;
    if (!canvas) { setLoading(false); return; }
    const W = 1275, H = 1650; // 8.5x11 @ 150dpi
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Off-white paper background
    ctx.fillStyle = "#F5F2E8";
    ctx.fillRect(0, 0, W, H);

    // Deep forest block at top
    ctx.fillStyle = "#123A1A";
    ctx.fillRect(0, 0, W, 180);

    // Red accent stripe
    ctx.fillStyle = "#E53935";
    ctx.fillRect(0, 180, W, 18);

    // Top-left: "THE LOCAL LOOT" wordmark
    ctx.fillStyle = "#F5F2E8";
    ctx.font = "900 60px 'Oswald', sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText("THE LOCAL LOOT", 70, 90);
    // Gold "$"
    ctx.fillStyle = "#D7A62A";
    ctx.font = "900 56px 'Oswald', sans-serif";
    ctx.fillText("$", W - 130, 90);

    // Huge FIND THE LOOT headline
    ctx.fillStyle = "#0F0F0F";
    ctx.font = "900 140px 'Oswald', sans-serif";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("FIND THE LOOT", 70, 400);
    ctx.fillStyle = "#76B82A";
    ctx.font = "900 160px 'Oswald', sans-serif";
    ctx.fillText(`IN ${city.toUpperCase()}.`, 70, 560);

    // Sub-tagline
    ctx.fillStyle = "#0F0F0F";
    ctx.font = "600 38px 'Outfit', sans-serif";
    ctx.fillText("Real money. Real clues. Real winners.", 70, 630);

    // Instructions
    ctx.fillStyle = "#0F0F0F";
    ctx.font = "700 36px 'Oswald', sans-serif";
    ctx.fillText("SCAN TO FOLLOW THE HUNT", 70, 820);
    ctx.fillStyle = "#123A1A";
    ctx.font = "400 26px 'Outfit', sans-serif";
    ctx.fillText("1. Point your camera at the code.", 70, 870);
    ctx.fillText("2. Tap the link → view live drops.", 70, 910);
    ctx.fillText("3. Create a hunter account and follow the clues.", 70, 950);

    // Fetch QR from backend
    try {
      const qrUrl = `${API}/qr?url=${encodeURIComponent(shareUrl)}&size=18`;
      const qr = await loadImage(qrUrl);
      // QR panel (white background + thick black border)
      const qrBoxSize = 520;
      const qrX = W - qrBoxSize - 80;
      const qrY = 720;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(qrX - 12, qrY - 12, qrBoxSize + 24, qrBoxSize + 24);
      ctx.fillStyle = "#0F0F0F";
      ctx.lineWidth = 0;
      // Corner markers (loot brutalist)
      const cm = 28;
      ctx.fillRect(qrX - 36, qrY - 36, cm, 8);
      ctx.fillRect(qrX - 36, qrY - 36, 8, cm);
      ctx.fillRect(qrX + qrBoxSize + 8, qrY - 36, cm, 8);
      ctx.fillRect(qrX + qrBoxSize + 28, qrY - 36, 8, cm);
      ctx.fillRect(qrX - 36, qrY + qrBoxSize + 28, cm, 8);
      ctx.fillRect(qrX - 36, qrY + qrBoxSize + 8, 8, cm);
      ctx.fillRect(qrX + qrBoxSize + 8, qrY + qrBoxSize + 28, cm, 8);
      ctx.fillRect(qrX + qrBoxSize + 28, qrY + qrBoxSize + 8, 8, cm);
      ctx.drawImage(qr, qrX, qrY, qrBoxSize, qrBoxSize);
    } catch (e) {
      ctx.fillStyle = "#E53935";
      ctx.font = "600 24px 'JetBrains Mono', monospace";
      ctx.fillText("QR failed to load", W - 440, 900);
    }

    // Sponsor logo below the QR (optional, nicer than printed URL)
    if (sponsorLogoUrl) {
      try {
        const logo = await loadImage(sponsorLogoUrl);
        const maxW = 360;
        const maxH = 130;
        const ratio = Math.min(maxW / logo.width, maxH / logo.height);
        const drawW = logo.width * ratio;
        const drawH = logo.height * ratio;
        // Centered under the QR panel
        const qrPanelCenterX = W - 80 - 520 / 2;
        const lx = qrPanelCenterX - drawW / 2;
        const ly = 1280 - drawH / 2;
        ctx.drawImage(logo, lx, ly, drawW, drawH);
      } catch (e) {
        // Logo couldn't load — silently skip; rest of poster still prints.
      }
    }

    // Divider
    ctx.strokeStyle = "#0F0F0F";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(70, 1380);
    ctx.lineTo(W - 70, 1380);
    ctx.stroke();

    // Bottom row
    ctx.fillStyle = "#0F0F0F";
    ctx.font = "900 64px 'Oswald', sans-serif";
    ctx.fillText("FIND IT FIRST.", 70, 1460);
    ctx.fillStyle = "#76B82A";
    ctx.fillText("KEEP IT.", 560, 1460);

    ctx.fillStyle = "#123A1A";
    ctx.font = "400 22px 'Outfit', sans-serif";
    ctx.fillText("#thelocalloot · hunts near you", 70, 1510);

    // Sponsor shoutout
    if (sponsorName) {
      ctx.fillStyle = "#0F0F0F";
      ctx.fillRect(0, 1570, W, 80);
      ctx.fillStyle = "#D7A62A";
      ctx.font = "700 28px 'Oswald', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        `PROUDLY SPONSORED BY ${sponsorName.toUpperCase()}`,
        W / 2,
        1620
      );
      ctx.textAlign = "left";
    }

    setDataUrl(canvas.toDataURL("image/png"));
    setLoading(false);
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `local-loot-poster-${city.replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const printPoster = () => {
    if (!dataUrl) return;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Popup blocked — use 'Save PNG' instead.");
      return;
    }
    w.document.write(
      `<html><head><title>Local Loot · ${city}</title><style>@page{size:letter;margin:0}body{margin:0}img{width:100%;height:auto;display:block}</style></head><body><img src="${dataUrl}" onload="window.print();window.close()"/></body></html>`
    );
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.85)" }}
      data-testid="qr-poster-modal"
    >
      <div className="loot-card-flat p-5 max-w-xl w-full" style={{ background: "#0F0F0F" }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline flex items-center gap-1">
              <QrCode size={12} /> Printable poster
            </div>
            <h3 className="font-display uppercase text-2xl">{city} poster</h3>
          </div>
          <button onClick={onClose} className="text-[#F5F2E8]/60" data-testid="qr-poster-close">
            <X size={20} />
          </button>
        </div>

        <div
          className="border border-[#F5F2E8]/10 overflow-hidden mb-3 relative"
          style={{ background: "#000", aspectRatio: "1275 / 1650" }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/60">
              <Loader2 className="animate-spin text-[#76B82A]" size={32} />
            </div>
          )}
          <canvas ref={canvasRef} className="w-full h-auto block" />
        </div>

        <p className="font-mono text-[10px] text-[#F5F2E8]/40 mb-3">
          8.5"×11" letter-size · QR deep-links to /hunts/{city}
          {sponsorLogoUrl ? <span className="text-[#76B82A]"> · sponsor logo embedded</span> : null}
        </p>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={download}
            disabled={!dataUrl}
            className="btn-loot text-xs"
            data-testid="qr-poster-download"
          >
            <Download size={14} className="inline mr-1" /> Save PNG
          </button>
          <button
            onClick={printPoster}
            disabled={!dataUrl}
            className="btn-gold text-xs"
            data-testid="qr-poster-print"
          >
            <Printer size={14} className="inline mr-1" /> Print
          </button>
        </div>
      </div>
    </div>
  );
}
